// ═══════════════════════════════════════════════════════════════════════
// DEPRECATED — to be removed in Phase 4.
// ═══════════════════════════════════════════════════════════════════════
// This procedural seed generator existed to make legacy Operating Systems (Applications) (created
// before the Architect→screen_data handoff) look populated. After
// Phase 1.5, only Operating Systems (Applications) WITHOUT an os_charters row use this path.
// Properly-provisioned Operating Systems (Applications) get real data from the Architect.
// Do not extend this file. Add new industries to the Architect's
// shape inference instead.
// ═══════════════════════════════════════════════════════════════════════
//
// SEED DATA GENERATOR
// ═══════════════════════════════════════════════════════════════════════
// Generates realistic fake data for any industry based on the Agentic Operating System schema.
// This runs at RENDER TIME — no AI call needed, instant results.
// The data makes the Agentic Operating System feel populated and alive from second one.
// ═══════════════════════════════════════════════════════════════════════

interface SeedRecord {
  id: string;
  name: string;
  stage?: string;
  value?: string;
  date?: string;
  status?: string;
  owner?: string;
  type?: string;
  [key: string]: any;
}

// R1 slim — only label + color are read downstream (the tenant renderer
// shows real fetched values and a neutral flat baseline; it never reads a
// seed value/change/trend). Dropping the dead fields keeps the seed honest.
interface SeedKPI {
  label: string;
  color: string;
}

interface SeedData {
  records: SeedRecord[];
  kpis: SeedKPI[];
  stages: string[];
}

const NAMES = {
  realty: ["The Palms Penthouse", "Marina Bay Villa", "Downtown Loft 4B", "Sunset Ridge Estate", "Harbor View Apartment", "Royal Gardens Unit 7", "Skyline Tower Suite", "Ocean Breeze Cottage"],
  finance: ["EUR/USD Long", "GBP/JPY Short", "BTC Spot Buy", "AAPL Call Spread", "Gold Futures Dec", "S&P 500 Index", "ETH/BTC Swing", "Crude Oil WTI"],
  healthcare: ["Sarah Mitchell", "James Okoro", "Priya Sharma", "Carlos Rivera", "Emma Thompson", "David Kimani", "Aisha Hassan", "Michael Chen"],
  legal: ["Anderson v. TechCorp", "Merger: Apex & Bolt", "IP Filing #4892", "Estate: Williams Trust", "NDA Review: StartupX", "Compliance Audit Q3", "Licensing Deal: MediaCo", "Patent Dispute #7731"],
  hospitality: ["Wedding: Kim & Marcus", "Corp Retreat: Accenture", "Birthday: The Johnsons", "Conference: TechSummit", "Private Dining: VIP", "Team Building: Google", "Anniversary: The Chens", "Product Launch: Nike"],
  sports: ["Marcus Williams", "Kwame Asante", "Tomás Silva", "Jordan Baptiste", "Yuki Tanaka", "Ibrahim Diallo", "Luca Moretti", "Andrei Volkov"],
  generic: ["Project Alpha", "Client Onboarding", "Q3 Strategy", "Partner Integration", "Product Launch v2", "Market Expansion", "Team Scaling", "Revenue Optimization"],
};

const STAGES_MAP: Record<string, string[]> = {
  realty: ["Lead", "Viewing Scheduled", "Offer Made", "Under Contract", "Closed"],
  finance: ["Research", "Entry Signal", "Open Position", "Take Profit", "Closed"],
  healthcare: ["Scheduled", "Checked In", "In Progress", "Complete", "Follow-up"],
  legal: ["Intake", "Discovery", "Negotiation", "Resolution", "Closed"],
  hospitality: ["Inquiry", "Quote Sent", "Confirmed", "In Progress", "Completed"],
  sports: ["Scouting", "Initial Contact", "Negotiation", "Contract Sent", "Signed"],
  generic: ["New", "In Progress", "Review", "Approved", "Complete"],
};

const OWNERS = ["King Kay", "Amira S.", "Marcus T.", "Valentina M.", "Chidi O."];
const TYPES = ["VIP", "Regular", "Referral", "Inbound", "Strategic"];

function detectIndustry(shape: string): string {
  const s = (shape || "").toLowerCase();
  if (s.includes("real") || s.includes("realty") || s.includes("property")) return "realty";
  if (s.includes("finance") || s.includes("forex") || s.includes("trading") || s.includes("invest")) return "finance";
  if (s.includes("health") || s.includes("clinic") || s.includes("medical") || s.includes("dental")) return "healthcare";
  if (s.includes("legal") || s.includes("law") || s.includes("attorney")) return "legal";
  if (s.includes("hotel") || s.includes("restaurant") || s.includes("hospitality") || s.includes("event")) return "hospitality";
  if (s.includes("sport") || s.includes("football") || s.includes("agent") || s.includes("athlet")) return "sports";
  return "generic";
}

function randomAmount(min: number, max: number): string {
  const val = Math.floor(Math.random() * (max - min) + min);
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(val);
}

function randomDate(daysBack: number): string {
  const d = new Date();
  d.setDate(d.getDate() - Math.floor(Math.random() * daysBack));
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function randomPercent(min: number, max: number): string {
  return `${(Math.random() * (max - min) + min).toFixed(1)}%`;
}

export function generateSeedData(industryShape: string, screenLabel?: string): SeedData {
  const industry = detectIndustry(industryShape);
  const names = NAMES[industry as keyof typeof NAMES] || NAMES.generic;
  const stages = STAGES_MAP[industry] || STAGES_MAP.generic;

  const records: SeedRecord[] = names.map((name, i) => ({
    id: `seed-${i}`,
    name,
    stage: stages[i % stages.length],
    value: industry === "finance" 
      ? randomAmount(-50000, 200000) 
      : randomAmount(5000, 500000),
    date: randomDate(30),
    status: i < 3 ? "Active" : i < 6 ? "Pending" : "Closed",
    owner: OWNERS[i % OWNERS.length],
    type: TYPES[i % TYPES.length],
  }));

  const kpis = generateKPIs(industry, screenLabel);

  return { records, kpis, stages };
}

function generateKPIs(industry: string, screenLabel?: string): SeedKPI[] {
  const kpiSets: Record<string, SeedKPI[]> = {
    realty: [
      { label: "Active Listings", color: "#6366F1" },
      { label: "Avg. Days on Market", color: "#10B981" },
      { label: "Pipeline Value", color: "#F59E0B" },
      { label: "Closings This Month", color: "#EC4899" },
    ],
    finance: [
      { label: "Total P&L (MTD)", color: "#10B981" },
      { label: "Win Rate", color: "#6366F1" },
      { label: "Open Positions", color: "#F59E0B" },
      { label: "Max Drawdown", color: "#EC4899" },
    ],
    healthcare: [
      { label: "Patients Today", color: "#6366F1" },
      { label: "Avg. Wait Time", color: "#10B981" },
      { label: "Revenue (MTD)", color: "#F59E0B" },
      { label: "No-Shows Rate", color: "#EC4899" },
    ],
    legal: [
      { label: "Active Matters", color: "#6366F1" },
      { label: "Billable Hours (MTD)", color: "#10B981" },
      { label: "Revenue Pipeline", color: "#F59E0B" },
      { label: "Avg. Resolution", color: "#EC4899" },
    ],
    hospitality: [
      { label: "Upcoming Events", color: "#6366F1" },
      { label: "Occupancy Rate", color: "#10B981" },
      { label: "Revenue (MTD)", color: "#F59E0B" },
      { label: "Guest Satisfaction", color: "#EC4899" },
    ],
    sports: [
      { label: "Active Players", color: "#6366F1" },
      { label: "Deals In Progress", color: "#10B981" },
      { label: "Commission Pipeline", color: "#F59E0B" },
      { label: "Avg. Deal Value", color: "#EC4899" },
    ],
    generic: [
      { label: "Active Projects", color: "#6366F1" },
      { label: "Completion Rate", color: "#10B981" },
      { label: "Revenue (MTD)", color: "#F59E0B" },
      { label: "Team Utilization", color: "#EC4899" },
    ],
  };

  return kpiSets[industry] || kpiSets.generic;
}
